import {
  CANONICAL_MATCH_TOLERANCE_M,
  CANONICAL_RACING_BASICS,
  GATE_PASS_DEBOUNCE_MS,
  GATE_PASS_MIN_RADIUS_M,
  GATE_PASS_RADIUS_M,
  GATE_PASS_RADIUS_SCALE,
  STRUCTURE_SIZE_PATTERN,
  type CanonicalGate,
} from "./canonical";
import type {
  FlightPayload,
  GatePass,
  GateRef,
  InfoPayload,
  RaceBBox,
  RaceDataset,
  RawStructure,
  TrajectoryPoint,
} from "./race-types";

const EARTH_R = 6378137.0;

export function toXY(lat: number, lon: number, refLat: number, refLon: number) {
  const cosLat = Math.cos((refLat * Math.PI) / 180);
  const x = ((lon - refLon) * Math.PI) / 180 * cosLat * EARTH_R;
  const y = ((lat - refLat) * Math.PI) / 180 * EARTH_R;
  return { x, y };
}

function metersBetween(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
) {
  const cosLat = Math.cos(((lat1 + lat2) / 2 * Math.PI) / 180);
  const dx = ((lon2 - lon1) * Math.PI) / 180 * cosLat * EARTH_R;
  const dy = ((lat2 - lat1) * Math.PI) / 180 * EARTH_R;
  return Math.sqrt(dx * dx + dy * dy);
}

function matchCanonical(lat: number, lon: number): CanonicalGate | null {
  let best: CanonicalGate | null = null;
  let bestD = Infinity;
  for (const g of CANONICAL_RACING_BASICS) {
    const d = metersBetween(lat, lon, g.lat, g.lon);
    if (d < bestD) {
      bestD = d;
      best = g;
    }
  }
  return bestD <= CANONICAL_MATCH_TOLERANCE_M ? best : null;
}

function resolveStructureSizeM(structure: RawStructure): number | null {
  if (typeof structure.size_m === "number" && structure.size_m > 0) {
    return structure.size_m;
  }
  if (typeof structure.class_name === "string") {
    const match = structure.class_name.match(STRUCTURE_SIZE_PATTERN);
    if (match) {
      const parsed = Number.parseFloat(match[1]);
      if (Number.isFinite(parsed) && parsed > 0) return parsed;
    }
  }
  return null;
}

function gateCaptureRadiusM(structure: RawStructure): number {
  // Mirror ``parser/laps.py::_checkpoint_radius_m`` — size / 2 with a 4m floor.
  const sizeM = resolveStructureSizeM(structure);
  const fallbackSize = sizeM ?? 20.0;
  return Math.max(GATE_PASS_MIN_RADIUS_M, fallbackSize * GATE_PASS_RADIUS_SCALE);
}

export interface BuildOptions {
  info: InfoPayload;
  flightHistory: FlightPayload[];
  sessionKey: string;
}

/**
 * Pre-process a plugin bundle into the same shape the prototype's
 * preprocess.py produces (`window.RACE_DATA`) — meta, home, bbox, gates,
 * trajectory, passes. Unknown gates receive synthesised canonical indices
 * so we still draw them on the map.
 */
export function buildRaceDataset(opts: BuildOptions): RaceDataset | null {
  const { info, flightHistory, sessionKey } = opts;
  const home = info.home_point;
  if (!home) return null;
  const refLat = home.latitude;
  const refLon = home.longitude;

  const rawGates: RawStructure[] = (info.structures ?? []).filter(
    (s) => typeof s.class_name === "string" && s.class_name.includes("checkpoint"),
  );

  const gates: GateRef[] = [];
  for (const g of rawGates) {
    const xy = toXY(g.latitude, g.longitude, refLat, refLon);
    const canon = matchCanonical(g.latitude, g.longitude);
    gates.push({
      raw_id: g.id,
      idx: canon ? canon.idx : gates.length + 1000,
      lat: g.latitude,
      lon: g.longitude,
      alt: g.altitude_m,
      yaw: g.yaw_deg ?? 0,
      x: xy.x,
      y: xy.y,
      radius_m: gateCaptureRadiusM(g),
    });
  }

  // Re-order unmatched gates after matched ones to keep idx monotonic.
  const matched = gates
    .filter((g) => g.idx < 1000)
    .sort((a, b) => a.idx - b.idx);
  const unmatched = gates.filter((g) => g.idx >= 1000);
  const nextIdxStart = matched.length ? Math.max(...matched.map((g) => g.idx)) + 1 : 1;
  unmatched.forEach((g, i) => {
    g.idx = nextIdxStart + i;
  });
  const gatesOrdered = [...matched, ...unmatched];

  const trajectory: TrajectoryPoint[] = [];
  let t0: number | null = null;
  for (const frame of flightHistory) {
    const vehicles = frame.vehicles ?? [];
    const v = vehicles.find((x) => x.entity_id === 1);
    if (!v) continue;
    const ts = typeof frame.timestamp_ms === "number" ? frame.timestamp_ms : 0;
    if (t0 === null) t0 = ts;
    const xy = toXY(v.latitude, v.longitude, refLat, refLon);
    const speed = Math.sqrt(
      v.velocity_x * v.velocity_x +
        v.velocity_y * v.velocity_y +
        v.velocity_z * v.velocity_z,
    );
    trajectory.push({
      t: ts - t0,
      lat: v.latitude,
      lon: v.longitude,
      x: xy.x,
      y: xy.y,
      alt: v.altitude,
      yaw: v.yaw,
      pitch: v.pitch,
      roll: v.roll,
      vx: v.velocity_x,
      vy: v.velocity_y,
      vz: v.velocity_z,
      speed_ms: speed,
    });
  }

  const passes = detectGatePasses(trajectory, gatesOrdered);

  const bbox = computeBBox(gatesOrdered);

  return {
    meta: {
      mission: info.mission_name ?? "",
      user: info.user_nickname ?? "",
      session_key: sessionKey,
      gate_pass_radius_m: GATE_PASS_RADIUS_M,
    },
    home: { lat: home.latitude, lon: home.longitude, alt: home.altitude_m },
    bbox,
    gates: gatesOrdered,
    trajectory,
    passes,
  };
}

// Default home derived from the prototype session — good enough to centre the
// canonical gates on the empty-state tactical map. Actual live sessions replace
// this with the home point from the info payload.
const DEFAULT_HOME = {
  lat: 50.34717397648372,
  lon: 16.63920170942154,
  alt: 362.0923634832984,
};

/**
 * Empty-state dataset: Racing-Basics canonical gates positioned around the
 * default home, with no trajectory. Used when no pilot is streaming so the
 * tactical map still shows the track layout.
 */
export function buildEmptyRacingBasicsDataset(): RaceDataset {
  const gates: GateRef[] = CANONICAL_RACING_BASICS.map((g, index) => {
    const xy = toXY(g.lat, g.lon, DEFAULT_HOME.lat, DEFAULT_HOME.lon);
    return {
      raw_id: index + 1,
      idx: g.idx,
      lat: g.lat,
      lon: g.lon,
      alt: g.alt_m,
      yaw: 0,
      x: xy.x,
      y: xy.y,
      radius_m: GATE_PASS_RADIUS_M,
    };
  }).sort((a, b) => a.idx - b.idx);

  return {
    meta: {
      mission: "Racing - Basics",
      user: "",
      session_key: "",
      gate_pass_radius_m: GATE_PASS_RADIUS_M,
    },
    home: { ...DEFAULT_HOME },
    bbox: computeBBox(gates),
    gates,
    trajectory: [],
    passes: [],
  };
}

function computeBBox(gates: GateRef[]): RaceBBox {
  const xs = gates.map((g) => g.x).concat([0]);
  const ys = gates.map((g) => g.y).concat([0]);
  const pad = 60;
  return {
    min_x: Math.min(...xs) - pad,
    max_x: Math.max(...xs) + pad,
    min_y: Math.min(...ys) - pad,
    max_y: Math.max(...ys) + pad,
  };
}

function horizontalDistance(p: TrajectoryPoint, g: GateRef) {
  const dx = p.x - g.x;
  const dy = p.y - g.y;
  return Math.sqrt(dx * dx + dy * dy);
}

function interpolateEntryT(
  prevT: number,
  currT: number,
  prevHoriz: number,
  currHoriz: number,
  radiusM: number,
): number {
  // Mirror ``parser/laps._interpolate_entry_elapsed_seconds`` — linearly
  // approximate when the horizontal distance curve crossed the radius so
  // the pass time doesn't bias toward whichever sample happened to land
  // inside first.
  const dd = currHoriz - prevHoriz;
  if (Math.abs(dd) < 1e-9) return currT;
  const ratio = Math.min(1, Math.max(0, (radiusM - prevHoriz) / dd));
  return prevT + (currT - prevT) * ratio;
}

interface GateState {
  wasIn: boolean;
  lastPassT: number | null;
  prevHoriz: number;
}

function freshGateStates(gates: GateRef[]): Map<number, GateState> {
  return new Map(
    gates.map((g) => [g.raw_id, { wasIn: false, lastPassT: null, prevHoriz: Infinity }]),
  );
}

function detectPassesRange(
  trajectory: TrajectoryPoint[],
  gates: GateRef[],
  states: Map<number, GateState>,
  startIdx: number,
  passes: GatePass[],
) {
  for (let i = startIdx; i < trajectory.length; i++) {
    const p = trajectory[i];
    const prev = i > 0 ? trajectory[i - 1] : null;
    for (const g of gates) {
      const state = states.get(g.raw_id)!;
      const horiz = horizontalDistance(p, g);
      const vert = Math.abs(p.alt - g.alt);
      const isIn = horiz <= g.radius_m && vert <= g.radius_m;
      if (isIn && !state.wasIn) {
        let entryT = p.t;
        if (prev) {
          entryT = interpolateEntryT(
            prev.t,
            p.t,
            state.prevHoriz,
            horiz,
            g.radius_m,
          );
        }
        const debounced =
          state.lastPassT === null ||
          entryT - state.lastPassT >= GATE_PASS_DEBOUNCE_MS;
        if (debounced) {
          passes.push({ t: entryT, raw_id: g.raw_id, gate_idx: g.idx, d: horiz });
          state.lastPassT = entryT;
        }
      }
      state.wasIn = isIn;
      state.prevHoriz = horiz;
    }
  }
}

export function detectGatePasses(
  trajectory: TrajectoryPoint[],
  gates: GateRef[],
): GatePass[] {
  const states = freshGateStates(gates);
  const passes: GatePass[] = [];
  detectPassesRange(trajectory, gates, states, 0, passes);
  return passes;
}

/**
 * Append new flight frames to an existing dataset (live mode). Reuses the
 * same (refLat, refLon) origin so x/y stay continuous with what was already
 * drawn. Returns a new dataset with appended trajectory / detected passes.
 */
export function appendFlightFrames(
  dataset: RaceDataset,
  frames: FlightPayload[],
  tOriginMs: number | null,
): { dataset: RaceDataset; tOriginMs: number | null } {
  if (!frames.length) return { dataset, tOriginMs };
  const refLat = dataset.home.lat;
  const refLon = dataset.home.lon;

  let t0 = tOriginMs;
  const newPoints: TrajectoryPoint[] = [];
  for (const frame of frames) {
    const v = (frame.vehicles ?? []).find((x) => x.entity_id === 1);
    if (!v) continue;
    const ts = typeof frame.timestamp_ms === "number" ? frame.timestamp_ms : 0;
    if (t0 === null) t0 = ts;
    const xy = toXY(v.latitude, v.longitude, refLat, refLon);
    const speed = Math.sqrt(
      v.velocity_x * v.velocity_x +
        v.velocity_y * v.velocity_y +
        v.velocity_z * v.velocity_z,
    );
    newPoints.push({
      t: ts - t0,
      lat: v.latitude,
      lon: v.longitude,
      x: xy.x,
      y: xy.y,
      alt: v.altitude,
      yaw: v.yaw,
      pitch: v.pitch,
      roll: v.roll,
      vx: v.velocity_x,
      vy: v.velocity_y,
      vz: v.velocity_z,
      speed_ms: speed,
    });
  }
  if (!newPoints.length) return { dataset, tOriginMs: t0 };

  const trajectory = dataset.trajectory.concat(newPoints);

  // Replay the full trajectory through the detector so debounce + entry
  // interpolation stay consistent with the analysis pipeline. The per-gate
  // state machine only needs ~8 entries and each frame visits each gate
  // once, so this stays cheap even for long live sessions.
  const passes = detectGatePasses(trajectory, dataset.gates);

  return {
    dataset: { ...dataset, trajectory, passes },
    tOriginMs: t0,
  };
}
